import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";

const AddJourneyForm = () => {
  const location = useLocation();
  const journeyType = location.state?.journeyType || "core";
  const navigate = useNavigate();
  const user = useSelector((state) => state?.user);

  const [tagOptions, setTagOptions] = useState([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTag, setSearchTag] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    tags: [], // Now an array for multiple selections
    status: "Active",
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch tags from API
  useEffect(() => {
    const fetchTags = async () => {
      try {
        setIsLoadingTags(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/tags/get-tags`,
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
              "Content-Type": "application/json",
            },
          }
        );
        console.log("🚀 ~ fetchTags ~ response:", response);
        
        if (response.status === 200) {
          // Updated to handle the correct response format
          const tags = response.data?.data?.map(tag => tag.name) || [];
          setTagOptions(tags);
        }
      } catch (error) {
        console.error("Error fetching tags:", error);
        toast.error("Failed to load tags");
        // Fallback to some default tags if API fails
        setTagOptions([
          "Mindfulness", "Leadership", "Communication", 
          "Personal Growth", "Team Building", "Productivity"
        ]);
      } finally {
        setIsLoadingTags(false);
      }
    };

    fetchTags();
  }, [user.token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleTagChange = (tag) => {
    if (formData.tags.includes(tag)) {
      // Remove tag if already selected
      setFormData({
        ...formData,
        tags: formData.tags.filter(item => item !== tag)
      });
    } else {
      // Add tag if not already selected
      setFormData({
        ...formData,
        tags: [...formData.tags, tag]
      });
    }
  };
  
  const handleTagSearch = (e) => {
    setSearchTag(e.target.value);
  };
  
  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };
  
  const filteredTags = tagOptions.filter(tag => 
    tag.toLowerCase().includes(searchTag.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.dropdown-container')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Journey name is required");
      return;
    }
    
    if (formData.tags.length === 0) {
      toast.error("At least one tag is required");
      return;
    }

    try {
      setIsSubmitting(true);
      
      const journeyData = {
        ...formData,
        tag: formData.tags.join(", "), // Convert array to comma-separated string for backend
        businessId: user.user?.businessId,
        businessIdentifier: user.user?.businessIdentifier,
        type: journeyType,
        exercises: 0, // Initial count is zero
      };
      
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/futureSelf/add-journey`,
        journeyData,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 201 || response.status === 200) {
        toast.success("Journey created successfully!");
        navigate("/journeys");
      }
    } catch (error) {
      console.error("Error creating journey:", error);
      toast.error(error.response?.data?.message || "Failed to create journey");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="d-flex justify-content-start">
      <div
        className="p-4"
        style={{
          maxWidth: "700px",
          width: "100%",
          marginLeft: "0", // flush to the left
        }}
      >
      <div style={sectionTitleStyle}>
          <h3 style={titleTextStyle}> Add New {journeyType.charAt(0).toUpperCase() + journeyType.slice(1)} Journey</h3>
        </div>
      <div className="bg-white rounded shadow-sm p-4">
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="name" className="form-label">
              Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="mb-3">
            <label className="form-label">
              Number of Exercises
            </label>
            <input
              type="text"
              className="form-control"
              value="0 (Auto-Count)"
              disabled
              readOnly
            />
            <small className="text-muted">
              This will be automatically updated as exercises are added
            </small>
          </div>

          <div className="mb-3">
            <label htmlFor="tags" className="form-label">
              Tags <span className="text-danger">*</span>
            </label>
            <div className="dropdown-container position-relative">
              <div 
                className="form-control d-flex flex-wrap align-items-center" 
                onClick={toggleDropdown}
                style={{ cursor: "pointer", minHeight: "60px" }}
              >
                {formData.tags.length === 0 ? (
                  <span className="text-muted">Select tags...</span>
                ) : (
                  formData.tags.map((tag, index) => (
                    <span 
                      key={index} 
                      className="badge bg-primary me-1 mb-1"
                      style={{ fontSize: "0.9rem", padding: "5px 10px" }}
                    >
                      {tag}
                      <span 
                        className="ms-1" 
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTagChange(tag);
                        }}
                      >
                        &times;
                      </span>
                    </span>
                  ))
                )}
              </div>
              
              {showDropdown && (
                <div 
                  className="dropdown-menu d-block position-absolute w-100 shadow"
                  style={{ maxHeight: "250px", overflowY: "auto", zIndex: 1000 }}
                >
                  <div className="px-3 py-2">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search tags..."
                      value={searchTag}
                      onChange={handleTagSearch}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="dropdown-divider"></div>
                  {isLoadingTags ? (
                    <div className="text-center py-2">Loading tags...</div>
                  ) : filteredTags.length > 0 ? (
                    filteredTags.map((tag, index) => (
                      <div 
                        key={index}
                        className="dropdown-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTagChange(tag);
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`tag-${index}`}
                            checked={formData.tags.includes(tag)}
                            onChange={() => handleTagChange(tag)}
                          />
                          <label 
                            className="form-check-label" 
                            htmlFor={`tag-${index}`}
                            style={{ width: "100%", cursor: "pointer" }}
                          >
                            {tag}
                          </label>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-2">No matching tags found</div>
                  )}
                </div>
              )}
            </div>
            {formData.tags.length === 0 && (
              <small className="text-danger">
                Please select at least one tag
              </small>
            )}
          </div>

          <div className="mb-3">
            <label htmlFor="status" className="form-label">
              Status <span className="text-danger">*</span>
            </label>
            <select
              className="form-select"
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          <div className="d-flex justify-content-end mt-4">
            <button
              type="button"
              className="btn btn-outline-secondary me-2"
              onClick={() => navigate("/journeys")}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="primaryBtn"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save Journey"}
            </button>
          </div>
        </form>
      </div>
    </div>
    </div>
  );
};


const sectionTitleStyle = {
  color: 'white',
  backgroundColor: '#064260',
  padding: '10px',
  borderRadius: '8px 8px 0 0',
  textAlign: 'left',
  fontWeight: 600,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const titleTextStyle = {
  fontSize: '21px',
  margin: 0,
};

export default AddJourneyForm;